import { type FC, useEffect, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import {
  SearchInput,
  FilterBar,
  StatusBadge,
  PriorityBadge,
  Drawer,
  EmptyState,
  SortSelect,
  useToast,
} from '@/components/base';
import { useAppContext } from '@/contexts/AppContext';
import { useFeeRules } from '@/hooks/useFeeRules';
import { useLegalProducts } from '@/hooks/useLegalProducts';
import { type Case, type CreateCaseInput, useCases } from '@/hooks/useCases';import CaseLegalCorePanel from '@/components/feature/CaseLegalCorePanel';
import CaseDeadlinesPanel from '@/components/feature/cases/CaseDeadlinesPanel';
import CaseAdmissionPanel from '@/components/feature/cases/CaseAdmissionPanel';
import CaseStrategicDossierPanel from '@/components/feature/cases/CaseStrategicDossierPanel';
import { compareDate, compareText, stableSort } from '@/lib/listSorting';


type DetailTab = 'Resumo' | 'Documentos' | 'Prazos' | 'Tarefas' | 'Financeiro' | 'Histórico' | 'Peças' | 'Estratégia' | 'Admissão';

type CaseFormState = {
  clientId: string;
  leadId: string;
  legalProductId: string;
  feeRuleId: string;
  title: string;
  caseNumber: string;
  court: string;
  courtDivision: string;
  legalArea: string;
  opposingParty: string;
  status: string;
  phase: string;
  priority: string;
  responsibleUser: string;
  caseValue: string;
  contractedFee: string;
  successFeePercentage: string;
  startDate: string;
  endDate: string;
  nextDeadlineAt: string;
  notes: string;
};

const caseStatusOptions = [
  { value: 'active', label: 'Ativo' },
  { value: 'waiting_client', label: 'Aguardando documentos' },
  { value: 'waiting_court', label: 'Aguardando decisão' },
  { value: 'hearing_scheduled', label: 'Audiência marcada' },
  { value: 'completed', label: 'Finalizado' },
  { value: 'archived', label: 'Arquivado' },
];

const casePriorityOptions = [
  { value: 'low', label: 'Baixa' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

const casePhaseOptions = [
  'Triagem',
  'Análise',
  'Petição Inicial',
  'Tutela de Urgência',
  'Defesa Escrita',
  'Recurso',
  'Investigação',
  'Audiência',
  'Sentença',
  'Execução',
];

const statusLabelMap = Object.fromEntries(caseStatusOptions.map((option) => [option.value, option.label]));
const priorityLabelMap = Object.fromEntries(casePriorityOptions.map((option) => [option.value, option.label]));

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const formatCurrency = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : currencyFormatter.format(value);

const formatDate = (value: string | null | undefined) => {
  if (!value) {
    return '—';
  }

  const parsed = Date.parse(value);
  if (Number.isFinite(parsed)) {
    return new Date(parsed).toLocaleDateString('pt-BR');
  }

  return value;
};

const toNullableText = (value: string) => {
  const text = value.trim();
  return text === '' ? null : text;
};

const toNullableNumber = (value: string) => {
  const text = value.trim();
  if (text === '') {
    return null;
  }

  const number = Number(text.replace(',', '.'));
  return Number.isFinite(number) ? number : null;
};

const getStatusLabel = (status: string) => statusLabelMap[status] ?? status;

const getPriorityLabel = (priority: string) => priorityLabelMap[priority] ?? priority;

const emptyCaseForm = (): CaseFormState => ({
  clientId: '',
  leadId: '',
  legalProductId: '',
  feeRuleId: '',
  title: '',
  caseNumber: '',
  court: '',
  courtDivision: '',
  legalArea: '',
  opposingParty: '',
  status: 'active',
  phase: 'Triagem',
  priority: 'normal',
  responsibleUser: 'Vinícius Borges Meschick',
  caseValue: '',
  contractedFee: '',
  successFeePercentage: '',
  startDate: new Date().toISOString().slice(0, 10),
  endDate: '',
  nextDeadlineAt: '',
  notes: '',
});

const caseToFormState = (caseItem: Case): CaseFormState => ({
  clientId: caseItem.clientId,
  leadId: caseItem.leadId ?? '',
  legalProductId: caseItem.legalProductId ?? '',
  feeRuleId: caseItem.feeRuleId ?? '',
  title: caseItem.title,
  caseNumber: caseItem.caseNumber ?? '',
  court: caseItem.court ?? '',
  courtDivision: caseItem.courtDivision ?? '',
  legalArea: caseItem.legalArea ?? '',
  opposingParty: caseItem.opposingParty ?? '',
  status: caseItem.status,
  phase: caseItem.phase ?? 'Triagem',
  priority: caseItem.priority,
  responsibleUser: caseItem.responsibleUser ?? '',
  caseValue: caseItem.caseValue === null || caseItem.caseValue === undefined ? '' : String(caseItem.caseValue),
  contractedFee:
    caseItem.contractedFee === null || caseItem.contractedFee === undefined ? '' : String(caseItem.contractedFee),
  successFeePercentage:
    caseItem.successFeePercentage === null || caseItem.successFeePercentage === undefined
      ? ''
      : String(caseItem.successFeePercentage),
  startDate: caseItem.startDate ?? '',
  endDate: caseItem.endDate ?? '',
  nextDeadlineAt: caseItem.nextDeadlineAt ?? '',
  notes: caseItem.notes ?? '',
});

const buildCreateCasePayload = (form: CaseFormState): CreateCaseInput => ({
  clientId: form.clientId.trim(),
  leadId: toNullableText(form.leadId),
  legalProductId: toNullableText(form.legalProductId),
  feeRuleId: toNullableText(form.feeRuleId),
  title: form.title.trim(),
  caseNumber: toNullableText(form.caseNumber),
  court: toNullableText(form.court),
  courtDivision: toNullableText(form.courtDivision),
  legalArea: toNullableText(form.legalArea),
  opposingParty: toNullableText(form.opposingParty),
  status: form.status,
  phase: toNullableText(form.phase),
  priority: form.priority,
  responsibleUser: toNullableText(form.responsibleUser),
  caseValue: toNullableNumber(form.caseValue),
  contractedFee: toNullableNumber(form.contractedFee),
  successFeePercentage: toNullableNumber(form.successFeePercentage),
  startDate: toNullableText(form.startDate),
  endDate: toNullableText(form.endDate),
  nextDeadlineAt: toNullableText(form.nextDeadlineAt),
  notes: toNullableText(form.notes),
});

const CasesPage: FC = () => {
  const { addToast } = useToast();
  const { clients, clientsLoading, clientsError } = useAppContext();
  const { cases, isLoadingCases, casesError, loadCases, createCase, updateCase, deleteCase } = useCases({ autoLoad: false });
  const { legalProducts, loadLegalProducts } = useLegalProducts({ autoLoad: false });
  const { feeRules, loadFeeRules } = useFeeRules({ autoLoad: false });

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('updated_desc');
  const [statusFilter, setStatusFilter] = useState('');
  const [phaseFilter, setPhaseFilter] = useState('');
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [activeTab, setActiveTab] = useState<DetailTab>('Resumo');
  const [showNewCaseDrawer, setShowNewCaseDrawer] = useState(false);
  const [isSavingCase, setIsSavingCase] = useState(false);
  const [editingCase, setEditingCase] = useState<Case | null>(null);
  const [deletingCaseId, setDeletingCaseId] = useState('');
  const [newCaseForm, setNewCaseForm] = useState<CaseFormState>(emptyCaseForm());

  useEffect(() => {
    void loadCases({ status: statusFilter || undefined });
  }, [loadCases, statusFilter]);

  useEffect(() => {
    void loadLegalProducts();
  }, [loadLegalProducts]);

  useEffect(() => {
    void loadFeeRules();
  }, [loadFeeRules]);

  const activeLegalProducts = useMemo(
    () => legalProducts.filter((product) => product.isActive),
    [legalProducts]
  );

  const activeFeeRules = useMemo(
    () => feeRules.filter((rule) => rule.isActive),
    [feeRules]
  );

  const clientOptions = useMemo(
    () => clients.map((client) => ({ label: client.name, value: client.id })),
    [clients]
  );

  const selectedCase = useMemo(
    () => cases.find((caseItem) => caseItem.id === selectedCaseId) ?? null,
    [cases, selectedCaseId]
  );

  const filteredCases = useMemo(() => {
    const query = search.trim().toLowerCase();

    const filtered = cases.filter((caseItem) => {
      if (!statusFilter && caseItem.status === 'archived') {
        return false;
      }

      if (statusFilter && caseItem.status !== statusFilter) {
        return false;
      }

      if (phaseFilter && caseItem.phase !== phaseFilter) {
        return false;
      }

      if (!query) {
        return true;
      }

      return [
        caseItem.clientName,
        caseItem.title,
        caseItem.legalProductName,
        caseItem.feeRuleName,
        caseItem.caseNumber,
        caseItem.court,
        caseItem.courtDivision,
        caseItem.legalArea,
        caseItem.opposingParty,
        caseItem.responsibleUser,
        caseItem.notes,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
    return stableSort(filtered, (left, right) => {
      if (sort === 'client') return compareText(left.clientName, right.clientName) || compareText(left.title, right.title);
      if (sort === 'number') return compareText(left.caseNumber, right.caseNumber) || compareText(left.title, right.title);
      if (sort === 'priority') return ({ urgent: 0, high: 1, normal: 2, low: 3 }[left.priority] ?? 9) - ({ urgent: 0, high: 1, normal: 2, low: 3 }[right.priority] ?? 9) || compareText(left.title, right.title);
      if (sort === 'created') return compareDate(left.createdAt, right.createdAt, 'desc') || compareText(left.title, right.title);
      return compareDate(left.updatedAt, right.updatedAt, 'desc') || compareText(left.title, right.title);
    });
  }, [cases, phaseFilter, search, sort, statusFilter]);

  const feeRuleOptions = useMemo(
    () =>
      activeFeeRules.map((rule) => ({
        value: rule.id,
        label: rule.legalProductName ? `${rule.legalProductName} — ${rule.name}` : rule.name,
      })),
    [activeFeeRules]
  );

  const openNewCaseDrawer = () => {
    setSelectedCaseId('');
    setEditingCase(null);
    setActiveTab('Resumo');
    setNewCaseForm(emptyCaseForm());
    setShowNewCaseDrawer(true);
  };

  const openEditCase = (caseItem: Case) => {
    setSelectedCaseId('');
    setEditingCase(caseItem);
    setActiveTab('Resumo');
    setNewCaseForm(caseToFormState(caseItem));
    setShowNewCaseDrawer(true);
  };

  const closeNewCaseDrawer = () => {
    if (isSavingCase) {
      return;
    }

    setShowNewCaseDrawer(false);
    setEditingCase(null);
    setNewCaseForm(emptyCaseForm());
  };

  const handleSaveCase = async () => {
    if (isSavingCase) {
      return;
    }

    if (!newCaseForm.clientId.trim()) {
      addToast('Preencha o cliente do caso.', 'warning');
      return;
    }

    if (!newCaseForm.title.trim()) {
      addToast('Preencha o título do caso.', 'warning');
      return;
    }

    setIsSavingCase(true);

    try {
      const payload = buildCreateCasePayload(newCaseForm);

      if (editingCase) {
        await updateCase(editingCase.id, payload);
        addToast('Caso atualizado com sucesso.', 'success');
      } else {
        await createCase(payload);
        addToast('Caso criado com sucesso.', 'success');
      }

      setShowNewCaseDrawer(false);
      setEditingCase(null);
      setNewCaseForm(emptyCaseForm());
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível salvar o caso.', 'error');
    } finally {
      setIsSavingCase(false);
    }
  };

  const handleArchiveCase = async (caseItem: Case) => {
    if (isSavingCase || deletingCaseId) {
      return;
    }

    const confirmed = window.confirm(
      'Tem certeza que deseja arquivar este caso? Esta ação removerá o caso da listagem principal, mas manterá o registro no banco para auditoria.'
    );

    if (!confirmed) {
      return;
    }

    setDeletingCaseId(caseItem.id);

    try {
      await deleteCase(caseItem.id);
      addToast('Caso arquivado com sucesso.', 'success');

      if (selectedCaseId === caseItem.id) {
        setSelectedCaseId('');
      }

      if (editingCase?.id === caseItem.id) {
        setShowNewCaseDrawer(false);
        setEditingCase(null);
        setNewCaseForm(emptyCaseForm());
      }
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível arquivar o caso.', 'error');
    } finally {
      setDeletingCaseId('');
    }
  };

  const handleRetryCases = () => {
    void loadCases({ status: statusFilter || undefined });
  };

  const saveButtonClassName = `px-3 py-1.5 rounded-lg text-xs font-medium text-background-50 transition-colors cursor-pointer whitespace-nowrap ${
    isSavingCase ? 'bg-primary-300' : 'bg-primary-500 hover:bg-primary-600'
  }`;

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-foreground-900">Casos / Processos</h2>
            <p className="text-sm text-foreground-500 mt-0.5">
              {isLoadingCases ? 'Carregando casos...' : `${filteredCases.length} casos · Controle operacional`}
            </p>
          </div>
          <button
            onClick={openNewCaseDrawer}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-500 text-background-50 rounded-lg text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i> Novo Caso
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Buscar por cliente, título, produto ou honorários..."
            className="sm:w-72"
          />
          <FilterBar
            filters={[
              {
                key: 'status',
                label: 'Status',
                options: caseStatusOptions.map((option) => ({ label: option.label, value: option.value })),
                value: statusFilter,
                onChange: setStatusFilter,
              },
              {
                key: 'phase',
                label: 'Fase',
                options: casePhaseOptions.map((phase) => ({ label: phase, value: phase })),
                value: phaseFilter,
                onChange: setPhaseFilter,
              },
            ]}
          />
          <SortSelect value={sort} onChange={setSort} options={[{ value: 'client', label: 'Cliente' }, { value: 'number', label: 'Número' }, { value: 'priority', label: 'Prioridade' }, { value: 'updated_desc', label: 'Última atualização' }, { value: 'created', label: 'Cadastro' }]} />
        </div>

        {isLoadingCases && (
          <div className="rounded-lg border border-dashed border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-500">
            Carregando casos operacionais...
          </div>
        )}

        {casesError && !isLoadingCases && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center justify-between gap-3">
            <span>{casesError}</span>
            <button
              onClick={handleRetryCases}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {filteredCases.length === 0 && !isLoadingCases && !casesError && (
          <div className="rounded-xl border border-dashed border-background-200 bg-background-50 p-8 text-center">
            <p className="text-sm font-medium text-foreground-700">Nenhum caso encontrado</p>
            <p className="text-xs text-foreground-500 mt-1">Ajuste a busca ou crie um novo caso.</p>
          </div>
        )}

        <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    Cliente
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap hidden lg:table-cell">
                    Produto
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    Status
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap hidden md:table-cell">
                    Fase
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    Prioridade
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap hidden lg:table-cell">
                    Valor
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap hidden lg:table-cell">
                    Honorários
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredCases.length === 0 ? (
                  <tr>
                      <td colSpan={8}>
                      <EmptyState
                        icon="ri-scales-line"
                        title="Nenhum caso encontrado"
                        description="Ajuste os filtros ou crie um novo caso."
                        action={{
                          label: 'Novo Caso',
                          onClick: openNewCaseDrawer,
                          icon: 'ri-add-line',
                        }}
                      />
                    </td>
                  </tr>
                ) : (
                  filteredCases.map((caseItem) => (
                    <tr
                      key={caseItem.id}
                      className="border-b border-background-100 hover:bg-background-50 transition-colors cursor-pointer"
                      onClick={() => {
                        setSelectedCaseId(caseItem.id);
                        setActiveTab('Resumo');
                      }}
                    >
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-foreground-800 whitespace-nowrap">
                            {caseItem.clientName ?? '—'}
                          </p>
                          <p className="text-xs text-foreground-400 whitespace-nowrap">
                            {caseItem.title || 'Sem título'}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <div>
                          <span className="text-sm text-foreground-600 whitespace-nowrap">
                            {caseItem.legalProductName ?? '—'}
                          </span>
                          {caseItem.feeRuleName && (
                            <p className="text-[11px] text-foreground-400 whitespace-nowrap">
                              {caseItem.feeRuleName}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={getStatusLabel(caseItem.status)} />
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-sm text-foreground-600 whitespace-nowrap">
                          {caseItem.phase ?? '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <PriorityBadge priority={getPriorityLabel(caseItem.priority)} size="sm" />
                      </td>
                      <td className="px-4 py-3 text-right hidden lg:table-cell">
                        <span className="text-sm font-semibold text-foreground-800 whitespace-nowrap">
                          {formatCurrency(caseItem.caseValue)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right hidden lg:table-cell">
                        <span className="text-sm font-semibold text-foreground-800 whitespace-nowrap">
                          {formatCurrency(caseItem.contractedFee)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={(event) => {
                              event.stopPropagation();
                              openEditCase(caseItem);
                            }}
                            disabled={isSavingCase || Boolean(deletingCaseId)}
                            className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            Editar
                          </button>
                          <button
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleArchiveCase(caseItem);
                            }}
                            disabled={isSavingCase || Boolean(deletingCaseId)}
                            className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-red-200 text-red-700 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {deletingCaseId === caseItem.id ? 'Arquivando...' : 'Arquivar'}
                          </button>
                          <button
                            onClick={(event) => {
                              event.stopPropagation();
                              setSelectedCaseId(caseItem.id);
                              setActiveTab('Resumo');
                            }}
                            className="w-7 h-7 rounded-lg flex items-center justify-center text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                          >
                            <i className="ri-arrow-right-s-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <Drawer
          isOpen={!!selectedCase}
          onClose={() => {
            setSelectedCaseId('');
            setActiveTab('Resumo');
          }}
          title={selectedCase ? `Caso: ${selectedCase.title || selectedCase.clientName || 'Detalhes'}` : ''}
          width="w-[560px]"
          footer={
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => {
                  if (selectedCase) {
                    openEditCase(selectedCase);
                  }
                }}
                disabled={!selectedCase || isSavingCase || Boolean(deletingCaseId)}
                className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <i className="ri-edit-line mr-1"></i> Editar
              </button>
              <button
                onClick={() => {
                  if (selectedCase) {
                    void handleArchiveCase(selectedCase);
                  }
                }}
                disabled={!selectedCase || isSavingCase || Boolean(deletingCaseId)}
                className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-red-200 text-red-700 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {deletingCaseId === selectedCase?.id ? 'Arquivando...' : 'Arquivar'}
              </button>
              <button
                disabled
                className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-not-allowed whitespace-nowrap opacity-60"
              >
                <i className="ri-file-text-line mr-1"></i> Gerar Contrato
              </button>
              <button
                disabled
                className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-not-allowed whitespace-nowrap opacity-60"
              >
                <i className="ri-file-copy-line mr-1"></i> Gerar Procuração
              </button>
              <button
                disabled
                className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-not-allowed whitespace-nowrap opacity-60"
              >
                <i className="ri-add-line mr-1"></i> Criar Tarefa
              </button>
            </div>
          }
        >
          {selectedCase && (
            <div className="space-y-5">
              <div className="flex border-b border-background-200">
                {(['Resumo', 'Documentos', 'Prazos', 'Tarefas', 'Financeiro', 'Histórico', 'Peças', 'Estratégia', 'Admissão'] as DetailTab[]).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-3 py-2 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap border-b-2 -mb-px ${
                      activeTab === tab
                        ? 'border-primary-500 text-primary-700'
                        : 'border-transparent text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {activeTab === 'Resumo' && (
                <div className="space-y-4">
                  <CaseLegalCorePanel
                    caseItem={selectedCase}
                    mode="summary"
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusBadge status={getStatusLabel(selectedCase.status)} />
                    <PriorityBadge priority={getPriorityLabel(selectedCase.priority)} />
                  </div>
                  <p className="text-sm text-foreground-600 bg-background-100 rounded-lg p-3">
                    {selectedCase.title || 'Sem título informado.'}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Cliente</p>
                      <p className="text-sm text-foreground-700">{selectedCase.clientName ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Produto Jurídico</p>
                      <p className="text-sm text-foreground-700">{selectedCase.legalProductName ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Regra de Honorários</p>
                      <p className="text-sm text-foreground-700">{selectedCase.feeRuleName ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Fase</p>
                      <p className="text-sm text-foreground-700">{selectedCase.phase ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Responsável</p>
                      <p className="text-sm text-foreground-700">{selectedCase.responsibleUser ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Início</p>
                      <p className="text-sm text-foreground-700">{formatDate(selectedCase.startDate)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Próximo Prazo</p>
                      <p className="text-sm text-foreground-700">{formatDate(selectedCase.nextDeadlineAt)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-foreground-400 uppercase tracking-wider">Valor do Caso</p>
                      <p className="text-sm font-semibold text-foreground-900">
                        {formatCurrency(selectedCase.caseValue)}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] text-foreground-400 uppercase tracking-wider mb-2">Observações</p>
                    <p className="text-sm text-foreground-600 bg-background-100 rounded-lg p-3">
                      {selectedCase.notes || 'Sem observações registradas.'}
                    </p>
                  </div>
                </div>
              )}

              {activeTab === 'Documentos' && (
                <div className="space-y-2">
                  {[
                    { label: 'Número do processo', value: selectedCase.caseNumber },
                    { label: 'Vara / Tribunal', value: selectedCase.court },
                    { label: 'Divisão / Câmara', value: selectedCase.courtDivision },
                    { label: 'Área jurídica', value: selectedCase.legalArea },
                    { label: 'Parte contrária', value: selectedCase.opposingParty },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between p-2.5 bg-background-100 rounded-lg">
                      <span className="text-sm text-foreground-700">{item.label}</span>
                      <span className="text-sm text-foreground-500">{item.value ?? '—'}</span>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'Tarefas' && (
                <div className="space-y-2">
                  {[
                    { label: 'Status do caso', value: getStatusLabel(selectedCase.status) },
                    { label: 'Fase atual', value: selectedCase.phase ?? '—' },
                    { label: 'Responsável', value: selectedCase.responsibleUser ?? '—' },
                    { label: 'Próximo prazo', value: formatDate(selectedCase.nextDeadlineAt) },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between p-2.5 bg-background-100 rounded-lg">
                      <span className="text-sm text-foreground-700">{item.label}</span>
                      <span className="text-sm text-foreground-500">{item.value}</span>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'Prazos' && (
                <CaseDeadlinesPanel
                  caseItem={selectedCase}
                  onChanged={() =>
                    loadCases({
                      status:
                        statusFilter ||
                        undefined,
                    })
                  }
                />
              )}

              {activeTab === 'Financeiro' && (
                <div className="space-y-2">
                  {[
                    { label: 'Valor do caso', value: formatCurrency(selectedCase.caseValue) },
                    { label: 'Honorário contratado', value: formatCurrency(selectedCase.contractedFee) },
                    {
                      label: 'Êxito (%)',
                      value: selectedCase.successFeePercentage === null ? '—' : `${selectedCase.successFeePercentage.toFixed(2)}%`,
                    },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between p-2.5 bg-background-100 rounded-lg">
                      <span className="text-sm text-foreground-700">{item.label}</span>
                      <span className="text-sm font-semibold text-foreground-800 whitespace-nowrap">{item.value}</span>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'Histórico' && (
                <CaseLegalCorePanel
                  caseItem={selectedCase}
                  mode="history"
                />
              )}

              {activeTab === 'Peças' && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 p-2.5 bg-background-100 rounded-lg">
                    <i className="ri-file-text-line text-primary-500"></i>
                    <span className="text-sm text-foreground-700">Nenhuma peça vinculada ainda.</span>
                  </div>
                </div>
              )}

              {activeTab === 'Estratégia' && <CaseStrategicDossierPanel caseId={selectedCase.id} />}
              {activeTab === 'Admissão' && <CaseAdmissionPanel caseId={selectedCase.id} />}
            </div>
          )}
        </Drawer>

        <Drawer
          isOpen={showNewCaseDrawer}
          onClose={closeNewCaseDrawer}
          title={editingCase ? 'Editar Caso' : 'Novo Caso'}
          width="w-[760px]"
          footer={
            <div className="flex items-center gap-2">
              <button
                onClick={closeNewCaseDrawer}
                disabled={isSavingCase}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveCase}
                disabled={isSavingCase}
                className={saveButtonClassName}
              >
                {isSavingCase ? 'Salvando...' : editingCase ? 'Salvar Alterações' : 'Salvar Caso'}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            {!clientsLoading && clients.length === 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                Cadastre um cliente antes de criar um caso.
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-foreground-600 mb-1">Título *</label>
                <input
                  type="text"
                  value={newCaseForm.title}
                  onChange={(event) =>
                    setNewCaseForm((previous) => ({ ...previous, title: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="Título do caso"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-foreground-600 mb-1">Cliente *</label>
                <select
                  value={newCaseForm.clientId}
                  onChange={(event) =>
                    setNewCaseForm((previous) => ({ ...previous, clientId: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">{clientsLoading ? 'Carregando clientes...' : 'Selecionar cliente...'}</option>
                  {clientOptions.map((client) => (
                    <option key={client.value} value={client.value}>
                      {client.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Produto Jurídico</label>
                <select
                  value={newCaseForm.legalProductId}
                  onChange={(event) =>
                    setNewCaseForm((previous) => ({ ...previous, legalProductId: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">Sem produto jurídico</option>
                  {activeLegalProducts.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Regra de Honorários</label>
                <select
                  value={newCaseForm.feeRuleId}
                  onChange={(event) =>
                    setNewCaseForm((previous) => ({ ...previous, feeRuleId: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">Sem regra de honorários</option>
                  {feeRuleOptions.map((rule) => (
                    <option key={rule.value} value={rule.value}>
                      {rule.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Número do processo</label>
                <input
                  type="text"
                  value={newCaseForm.caseNumber}
                  onChange={(event) =>
                    setNewCaseForm((previous) => ({ ...previous, caseNumber: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="0001234-00.2026.8.26.0000"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Vara / Tribunal</label>
                <input
                  type="text"
                  value={newCaseForm.court}
                  onChange={(event) =>
                    setNewCaseForm((previous) => ({ ...previous, court: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="TJSP / 1ª Vara"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Divisão / Câmara</label>
                <input
                  type="text"
                  value={newCaseForm.courtDivision}
                  onChange={(event) =>
                    setNewCaseForm((previous) => ({ ...previous, courtDivision: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="Câmara Cível"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Área Jurídica</label>
                <input
                  type="text"
                  value={newCaseForm.legalArea}
                  onChange={(event) =>
                    setNewCaseForm((previous) => ({ ...previous, legalArea: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="Direito do Consumidor"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Parte contrária</label>
                <input
                  type="text"
                  value={newCaseForm.opposingParty}
                  onChange={(event) =>
                    setNewCaseForm((previous) => ({ ...previous, opposingParty: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="Banco X / Réu"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Status</label>
                <select
                  value={newCaseForm.status}
                  onChange={(event) =>
                    setNewCaseForm((previous) => ({ ...previous, status: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {caseStatusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Fase</label>
                <select
                  value={newCaseForm.phase}
                  onChange={(event) =>
                    setNewCaseForm((previous) => ({ ...previous, phase: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {casePhaseOptions.map((phase) => (
                    <option key={phase} value={phase}>
                      {phase}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Prioridade</label>
                <select
                  value={newCaseForm.priority}
                  onChange={(event) =>
                    setNewCaseForm((previous) => ({ ...previous, priority: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {casePriorityOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Responsável</label>
                <input
                  type="text"
                  value={newCaseForm.responsibleUser}
                  onChange={(event) =>
                    setNewCaseForm((previous) => ({ ...previous, responsibleUser: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="Nome do responsável"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Valor do Caso (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  value={newCaseForm.caseValue}
                  onChange={(event) =>
                    setNewCaseForm((previous) => ({ ...previous, caseValue: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="15000"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Honorário Contratado (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  value={newCaseForm.contractedFee}
                  onChange={(event) =>
                    setNewCaseForm((previous) => ({ ...previous, contractedFee: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="7500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Êxito (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={newCaseForm.successFeePercentage}
                  onChange={(event) =>
                    setNewCaseForm((previous) => ({
                      ...previous,
                      successFeePercentage: event.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="20"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Data de início</label>
                <input
                  type="date"
                  value={newCaseForm.startDate}
                  onChange={(event) =>
                    setNewCaseForm((previous) => ({ ...previous, startDate: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Data final</label>
                <input
                  type="date"
                  value={newCaseForm.endDate}
                  onChange={(event) =>
                    setNewCaseForm((previous) => ({ ...previous, endDate: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Próximo prazo</label>
                <input
                  type="date"
                  value={newCaseForm.nextDeadlineAt}
                  onChange={(event) =>
                    setNewCaseForm((previous) => ({ ...previous, nextDeadlineAt: event.target.value }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Notas</label>
              <textarea
                value={newCaseForm.notes}
                onChange={(event) =>
                  setNewCaseForm((previous) => ({ ...previous, notes: event.target.value }))
                }
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 resize-none"
                rows={3}
                placeholder="Observações internas sobre o caso"
              />
            </div>
          </div>
        </Drawer>
      </div>
    </AppLayout>
  );
};

export default CasesPage;
